import React from "react";
import Reveal from "@/components/Reveal";
import ClientMarquee from "@/components/ClientMarquee";
import ProjectCard from "@/components/ProjectCard";
import DirectorButton from "@/components/DirectorButton";
import Isotipo from "@/components/Isotipo";
import ServiceCard from "@/components/ServiceCard";
import { IMG, MARKETING_SERVICES, PROJECTS, METRICS, UI } from "@/data/site";
import { SEO } from "@/data/seo";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";

const Marketing = () => {
  const { t, lp } = useLang();
  const projects = PROJECTS.filter((p) => p.branch === "marketing");

  return (
    <div data-testid="marketing-page" className="bg-ink">
      <Seo {...SEO.marketing} />
      {/* Hero — green backstage */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-[72px]" style={{ backgroundColor: "#26775e" }}>
        <Isotipo className="absolute top-[16%] right-[8%] w-40 md:w-72 opacity-15" fill="#ffffff" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center w-full py-20">
          <div>
            <Reveal>
              <span className="font-display text-xs font-bold uppercase tracking-[0.35em] text-white/70">01 — {t({ es: "La agencia", en: "The agency" })}</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-display font-black uppercase text-white leading-[0.9] tracking-tight" style={{ fontSize: "clamp(2.1rem, 4.4vw, 4.2rem)" }}>
                {t({ es: "Marketing, Comunicación & Eventos", en: "Marketing, Communication & Events" })}
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-xl text-white/85 font-body text-lg md:text-xl">
                {t({ es: "20 años convirtiendo estrategia en resultados. Ecosistemas de marca completos para las grandes compañías.", en: "20 years turning strategy into results — end-to-end brand ecosystems for major companies." })}
              </p>
              <div className="mt-8"><DirectorButton href="#servicios" variant="solid" color="#ffffff" testId="marketing-hero-cta">{t({ es: "Ver servicios", en: "See services" })}</DirectorButton></div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden aspect-[4/5] shadow-2xl">
                <img src={IMG.eventos} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-8 -left-8 w-2/3 aspect-video rounded-2xl overflow-hidden border-4 border-brand-green shadow-2xl hidden md:block">
                <img src={IMG.meeting} alt="" className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Results / metrics band */}
      <section className="relative overflow-hidden border-b border-white/10" data-testid="marketing-metrics">
        <img src={IMG.growth} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
        <div className="absolute inset-0 bg-ink/85" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-display text-xs font-bold uppercase tracking-[0.3em] text-brand-green">{t({ es: "Resultados", en: "Results" })}</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-black uppercase text-white leading-none max-w-3xl">
              {t({ es: "Estrategia que se mide en negocio", en: "Strategy that shows in the numbers" })}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-6">
            {METRICS.map((m, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="border-l-2 pl-5" style={{ borderColor: "#26775e" }}>
                  <p className="font-display font-black text-white leading-none" style={{ fontSize: "clamp(3rem, 6vw, 5.5rem)" }}>
                    {m.value}<span className="text-brand-green">{m.suffix}</span>
                  </p>
                  <p className="mt-3 font-body text-white/55 text-sm md:text-base max-w-[16ch]">{t(m.label)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="servicios" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl font-black uppercase text-white mb-16 max-w-2xl leading-none">{t({ es: "Qué hacemos", en: "What we do" })}</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {MARKETING_SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.06}>
              <ServiceCard item={s} index={i} accent="#3fbf97" testId={`marketing-service-${s.id}`} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Eventos & Gastronomía showcase */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32" data-testid="eventos-gastronomia">
        <Reveal>
          <span className="font-display text-xs font-bold uppercase tracking-[0.3em] text-brand-green">{t({ es: "Verticales", en: "Verticals" })}</span>
          <h2 className="mt-4 font-display text-4xl md:text-6xl font-black uppercase text-white leading-none max-w-3xl">
            {t({ es: "Eventos & Gastronomía", en: "Events & Gastronomy" })}
          </h2>
          <p className="mt-6 max-w-2xl text-white/60 font-body text-lg">
            {t({ es: "De galas y jornadas profesionales a catas y maridajes para bodegas y marcas de bebidas premium. Experiencias que se recuerdan.", en: "From galas and professional summits to tastings and pairings for wineries and premium beverage brands. Experiences to remember." })}
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-5 auto-rows-[minmax(240px,auto)]">
          <Reveal className="lg:col-span-7 lg:row-span-2">
            <div className="group relative rounded-2xl overflow-hidden h-full min-h-[300px] border border-white/10">
              <img src={IMG.eventos} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              <div className="absolute bottom-0 p-8"><h3 className="font-display text-3xl md:text-4xl font-black uppercase text-white">{t({ es: "Eventos & Galas", en: "Events & Galas" })}</h3></div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="group relative rounded-2xl overflow-hidden h-full min-h-[240px] border border-white/10">
              <img src={IMG.gastronomy} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              <div className="absolute bottom-0 p-7"><h3 className="font-display text-2xl md:text-3xl font-black uppercase text-white">{t({ es: "Gastronomía", en: "Gastronomy" })}</h3></div>
            </div>
          </Reveal>
          <Reveal delay={0.18} className="lg:col-span-5">
            <div className="group relative rounded-2xl overflow-hidden h-full min-h-[240px] border border-white/10">
              <img src={IMG.wine} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              <div className="absolute bottom-0 p-7"><h3 className="font-display text-2xl md:text-3xl font-black uppercase text-white">{t({ es: "Catas & Maridajes", en: "Tastings & Pairings" })}</h3></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Clients */}
      <section className="border-y border-white/10 py-14">
        <ClientMarquee />
      </section>

      {/* Projects */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl font-black uppercase text-white mb-12 leading-none">{t({ es: "Casos de éxito", en: "Success stories" })}</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {projects.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}><ProjectCard project={p} className="h-full" /></Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 md:pb-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-[2rem] p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8" style={{ backgroundColor: "#26775e" }}>
              <h2 className="font-display text-3xl md:text-5xl font-black uppercase text-white max-w-xl text-balance leading-none">{t({ es: "Construyamos tu estrategia de marca", en: "Let's build your brand strategy" })}</h2>
              <DirectorButton to={lp("/contacto")} variant="solid" color="#ffffff" testId="marketing-cta">{t(UI.cta.talk)}</DirectorButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Marketing;
